/** True when `other` is a different step but has the same name (case-insensitive). */
export function sameStepNameConflict(step, other) {
    if (!step || !other || step.id === other.id) return false;
    if (!Object.prototype.hasOwnProperty.call(step, 'name') || !Object.prototype.hasOwnProperty.call(other, 'name')) {
        return false;
    }
    return String(step.name).toLowerCase() === String(other.name).toLowerCase();
}

export const moveElements = function (array, from, to) {
    if (from === to) return array;

    let newArray = array.slice();
    const droppedBack = to - from < 0;

    newArray.splice(to + (droppedBack ? 0 : 1), 0, array[from]);
    newArray = newArray.filter(function (_, i) {
        return i !== from + (droppedBack ? 1 : 0);
    });
    return newArray;
};

/**
 * Return a copy of `steps` with `step` inserted at `index` (0 prepends, `steps.length` appends).
 * Throw a RangeError for any other index rather than guess a position.
 */
export const insertStepAt = (steps, index, step) => {
    if (!Number.isInteger(index) || index < 0 || index > steps.length) {
        throw new RangeError(`Cannot insert a step at ${index} in a flow of ${steps.length} steps`);
    }
    return [...steps.slice(0, index), step, ...steps.slice(index)];
};

/**
 * Return the index to move the step at `fromIndex` to when it is dropped into the gap before
 * `gapIndex`, as `moveElements` expects it, or null when the drop would leave it where it is.
 */
export const getStepIndexForGapDrop = (fromIndex, gapIndex) => {
    const toIndex = fromIndex < gapIndex ? gapIndex - 1 : gapIndex;
    return toIndex === fromIndex ? null : toIndex;
};

export const applyVideoInterviewDataToStep = (step, videoData) => {
    if (!step || !videoData) return step;
    return {
        ...step,
        video_interview_data: {
            ...(step.video_interview_data || {}),
            interview_form: videoData.form,
            interview_deadline: videoData.interview_deadline,
        },
    };
};

export const TEMPORARY_STEP_ID_PREFIX = 'temp-id-';

export const isTemporaryStepId = (stepId) => String(stepId || '').startsWith(TEMPORARY_STEP_ID_PREFIX);

export function createTemporaryStepId() {
    return `${TEMPORARY_STEP_ID_PREFIX}${Date.now()}`;
}

/**
 * After a recruitment flow save, remap temporary step ids to the permanent ids returned by the API.
 * Preserves local step order and all other step fields.
 *
 * Temp steps are matched to saved steps by type plus case-insensitive name. That is usually safe
 * because sameStepNameConflict enforces unique names within a flow, but if the server returns a
 * normalized or localized name that differs from the local one, remapping is skipped for that step.
 * Unmapped temp ids remain until a page reload; persisted-only UI (e.g. the video "Edit questions"
 * link, which checks savedStepIds) stays hidden in the meantime.
 *
 * Returns null when step lists are empty, counts differ, or nothing changed — callers should keep
 * the previous flow (`reconcileFlowAfterSave(prev, saved) || prev`). These are intentional,
 * non-destructive fallbacks.
 */
export const reconcileFlowAfterSave = (currentFlow, savedFlow) => {
    if (!savedFlow?.steps?.length || !currentFlow?.steps?.length) {
        return null;
    }
    if (currentFlow.steps.length !== savedFlow.steps.length) {
        return null;
    }

    let changed = false;
    const steps = currentFlow.steps.map((step) => {
        if (!isTemporaryStepId(step.id)) {
            return step;
        }
        const localName = String(step.name ?? step.default_name ?? '').toLowerCase();
        const match = savedFlow.steps.find((candidate) => {
            if (currentFlow.steps.some(
                (s) => !isTemporaryStepId(s.id) && String(s.id) === String(candidate.id),
            )) {
                return false;
            }
            const candidateName = String(candidate.name ?? candidate.default_name ?? '').toLowerCase();
            return candidate.type === step.type && localName && localName === candidateName;
        });
        if (match && step.id !== match.id) {
            changed = true;
            return { ...step, id: match.id };
        }
        return step;
    });

    const flowFields = {};
    if (savedFlow.id != null && currentFlow.id !== savedFlow.id) {
        flowFields.id = savedFlow.id;
        changed = true;
    }

    return changed ? { ...currentFlow, ...flowFields, steps } : null;
};

export const transformStepForSave = (step = {}) => ({
    id: step?.id,
    i18n: step?.i18n,
    name: step?.name ?? step?.default_name,
    type: step?.type,
    deadline: step?.deadline,
    target_lead_time: step?.target_lead_time,
    anonymous_info: step?.anonymous_info,
    video_interview_data: step?.video_interview_data,
    locks_application: step?.locks_application,
});

export const mapFlowForSave = (flow = {}) => ({
    flow_id: flow?.id,
    name: flow?.name,
    flow: flow?.steps?.map(transformStepForSave),
});